@if (items.length && sizeSignal; as size) {
  @let hasNavigation = navigation && items.length > size;
  @let canGoPrevious = loop || activeSlide !== 0;
  @let canGoNext = loop || activeSlide <= items.length - size - 1;
  @let numberOfSlides = getNumberOfSlides(size);
  @let autoplayEnabled = isAutoplayEnabled();
  @let showAutoplayButton = showPlayPauseButton && autoplayEnabled && items.length > size;
  @let showIndicators = !hideIndicators && items.length > size;

  @if (title) {
    <h2>{{ title }}</h2>
  }

  <div
    class="carousel-panel"
    [ngClass]="'size-' + size"
    role="region"
    tabindex="0"
    (keydown)="onCarouselKeydown($event)"
  >
    <!-- Previous button -->
    @if (hasNavigation) {
      <button
        type="button"
        class="previous"
        tabindex="-1"
        (click)="prevSlide()"
        [disabled]="!canGoPrevious"
        [attr.aria-disabled]="!canGoPrevious"
        [attr.title]="'carousel.previousSlide' | cxTranslate"
      >
        <span class="visually-hidden">{{ 'carousel.previousSlide' | cxTranslate }}</span>
      </button>
    }

    <!-- Slides Container -->
    <div
      class="slides"
      role="group"
      [attr.aria-live]="autoplayEnabled ? 'polite' : 'off'"
      [attr.aria-atomic]="false"
      [attr.aria-label]="'carousel.slides' | cxTranslate"
    >
      <!-- Cloned Last Slide (Prepended) -->
      @if (lastClonedItem) {
        <div class="slide cloned" inert>
          @if (lastClonedItem | async; as data) {
            <div class="item" tabindex="-1">
              <ng-container
                *ngTemplateOutlet="
                  template;
                  context: { item: data, index: items.length - 1, length: items.length, size: size }
                "
              ></ng-container>
            </div>
          }
        </div>
      }

      <!-- Real Slides -->
      @for (item of items; track item; let i = $index) {
        @if (i % size === 0) {
          @let isActiveSlide = activeSlide >= i && activeSlide < i + size;
          <div class="slide" [class.active]="isActiveSlide" [attr.inert]="isActiveSlide ? null : ''">
            @for (item of items | slice: i : i + size; track item; let j = $index) {
              @if (item | async; as data) {
                <div class="item" [attr.tabindex]="isActiveSlide ? null : '-1'" [class.active]="isActiveSlide">
                  <ng-container
                    *ngTemplateOutlet="
                      template;
                      context: { item: data, index: i + j, length: items.length, size: size }
                    "
                  ></ng-container>
                </div>
              }
            }
          </div>
        }
      }

      <!-- Cloned First Slide (Appended) -->
      @if (firstClonedItem) {
        <div class="slide cloned" inert>
          @if (firstClonedItem | async; as data) {
            <div class="item" tabindex="-1">
              <ng-container
                *ngTemplateOutlet="template; context: { item: data, index: 0, length: items.length, size: size }"
              ></ng-container>
            </div>
          }
        </div>
      }
    </div>

    <!-- Next Button -->
    @if (hasNavigation) {
      <button
        type="button"
        class="next"
        tabindex="-1"
        (click)="nextSlide()"
        [disabled]="!canGoNext"
        [attr.aria-disabled]="!canGoNext"
        [attr.aria-label]="'carousel.nextSlide' | cxTranslate"
        [attr.title]="'carousel.nextSlide' | cxTranslate"
      >
        <span class="visually-hidden">{{ 'carousel.nextSlide' | cxTranslate }}</span>
      </button>
    }

    <!-- Autoplay Control Button -->
    @if (showAutoplayButton) {
      @let autoplayActive = isAutoplayActive();
      @let buttonLabel = autoplayActive ? 'Pause autoplay' : 'Start autoplay';
      <button
        type="button"
        class="autoplay"
        [class.auto-paused]="isAutoplayPaused()"
        tabindex="-1"
        (click)="toggleAutoplay()"
        (keydown.enter)="toggleAutoplay()"
        (keydown.space)="$event.preventDefault(); toggleAutoplay()"
        [attr.aria-label]="buttonLabel"
        [attr.title]="buttonLabel"
      >
        <span class="autoplay-icon" [class.paused]="!autoplayActive">
          <svg width="9" height="9" viewBox="0 0 9 9" fill="currentColor">
            @if (!autoplayActive) {
              <path d="M2 0 9 4.5 2 9z" />
            } @else {
              <path d="M0 0H3V9H0zM6 0H9V9H6z" />
            }
          </svg>
        </span>
      </button>
    }

    <!-- Indicators -->
    @if (showIndicators) {
      <div class="indicators" role="tablist">
        @for (item of items; track item; let i = $index) {
          @if (i % size === 0) {
            @let isActive = activeSlide >= i && activeSlide < i + size;
            @let slideNumber = i / size + 1;
            @let indicatorLabel =
              'carousel.slideNumber' | cxTranslate: { currentSlideNumber: slideNumber, numberOfSlides: numberOfSlides };
            @let itemTitle = (item | async)?.itemTitle;
            @let accessibleLabel =
              showIndicatorTitles && itemTitle ? itemTitle + ', ' + indicatorLabel : indicatorLabel;
            <button
              type="button"
              role="tab"
              (click)="goToSlide(i)"
              (keydown)="onIndicatorKeydown($event, i)"
              [attr.tabindex]="isActive ? -1 : -1"
              [attr.aria-selected]="isActive"
              [attr.aria-disabled]="isActive"
              [attr.aria-label]="accessibleLabel"
              [attr.title]="accessibleLabel"
            >
              @if (!showIndicatorTitles) {
                <span class="visually-hidden">{{ indicatorLabel }}</span>
              } @else {
                <span class="indicator-title">
                  {{ itemTitle || slideNumber }}
                </span>
              }
            </button>
          }
        }
      </div>
    }
  </div>
}
